<template>

<footer class="foot">

	<!-- geekgroup lockup: the real gg mark (its colour glyph on a dark badge,
	     exactly as Tally renders it) + a "part of geekgroup" line -->
	<a class="foot-gg" href="https://geekgroup.app" target="_blank" rel="noopener" aria-label="geekgroup">
		<span class="foot-gg-icon"></span>
		<span class="foot-gg-name">geek<span>group</span></span>
	</a>

	<!-- centred legal links: open the About / Privacy / Terms modal in-app -->
	<nav class="foot-links">
		<button class="foot-link" type="button" v-on:click="legal = 'about'">About</button>
		<button class="foot-link" type="button" v-on:click="legal = 'privacy'">Privacy</button>
		<button class="foot-link" type="button" v-on:click="legal = 'terms'">Terms</button>
	</nav>

	<!-- BoardGameGeek data credit: the real bgg mark -->
	<a class="foot-bgg" href="https://boardgamegeek.com" target="_blank" rel="noopener" aria-label="Powered by BoardGameGeek">
		<img :src="bggLogo" alt="Powered by BoardGameGeek" />
	</a>

	<app-legal v-if="legal" :page="legal" v-on:close="legal = false" />

</footer>

</template>

<script>

/*
	The slim site footer, on every screen. Mirrors Tally's footer treatment: the
	real geekgroup mark (its colour glyph on a dark circular badge, via CSS
	background like Tally) in a "part of geekgroup" lockup on the left, a centred
	About / Privacy / Terms link row, and the real BoardGameGeek mark on the
	right. Styled for boxart's LIGHT editorial palette: paper ground, hairline
	top divider, ink/muted text — none of Tally's dark chrome.
*/

import bggLogo from '../../assets/bgg.svg'
import Legal from './Legal.vue'

export default {

	components: {
		'app-legal': Legal
	},

	data: function() {

		return {
			bggLogo: bggLogo,
			// the open legal page ('about' | 'privacy' | 'terms') or false
			legal: false
		}

	}

}

</script>

<style scoped>

/* three tracks: brand left, links dead-centre, BGG credit right. The 1fr side
   tracks keep the links optically centred regardless of the marks' widths. */
.foot {
	display: grid;
	grid-template-columns: 1fr auto 1fr;
	align-items: center;
	column-gap: 24px;
	padding: 16px 28px;
	width: 100%;
	box-sizing: border-box;
	border-top: 1px solid var(--hairline);
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.04em;
	color: var(--muted);
}

/* the whole geekgroup lockup sits on the same dark chip as the BGG mark */
.foot-gg {
	display: flex;
	align-items: center;
	gap: 8px;
	text-decoration: none;
	color: var(--muted);
	justify-self: start;
	background: #024359;
	border-radius: 8px;
	padding: 6px 12px 6px 9px;
}

/* the gg colour glyph on its own dark circular badge — exactly Tally's
   treatment (the mark is bright and needs a dark backing) */
.foot-gg-icon {
	width: 22px;
	height: 22px;
	flex: none;
	background-image: url(../../assets/gg.svg);
	background-size: auto 20px;
	background-position: center;
	background-repeat: no-repeat;
}

/* the geekgroup wordmark, set in Barlow exactly like Tally (geek + group);
   adapted for the light footer — ink "geek", geekgroup-gold "group" */
.foot-gg-name {
	font-family: 'Barlow', sans-serif;
	font-size: 14px;
	font-weight: 700;
	letter-spacing: 1.5px;
	line-height: 14px;
	color: #fff;
}

.foot-gg-name span {
	color: #e0a92e;
}

/* the centred About / Privacy / Terms links */
.foot-links {
	display: flex;
	align-items: center;
	gap: 18px;
	justify-self: center;
}

.foot-link {
	font-family: var(--font-mono);
	font-size: 10px;
	font-weight: 600;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--muted);
	text-decoration: none;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
}

.foot-link:hover {
	color: var(--ink);
}

/* the BGG asset is the REVERSED (white) "Powered by BoardGameGeek" logo, made
   for dark backgrounds, so it needs a dark chip to read on the cream footer —
   the same treatment the geekgroup glyph gets on its dark badge. */
.foot-bgg {
	display: flex;
	align-items: center;
	text-decoration: none;
	justify-self: end;
	background: rgba(0, 0, 0, 0.6);
	border-radius: 8px;
	padding: 7px 11px;
}

.foot-bgg img {
	display: block;
	width: 96px;
	height: auto;
}

@media (max-width: 599px) {

	/* stack: links centred on top, the two marks centred in a row beneath */
	.foot {
		grid-template-columns: auto auto;
		justify-content: center;
		row-gap: 18px;
		column-gap: 28px;
		padding: 16px;
	}

	.foot-links {
		grid-column: 1 / -1;
		grid-row: 1;
	}

	.foot-gg {
		grid-column: 1;
		grid-row: 2;
		justify-self: end;
	}

	.foot-bgg {
		grid-column: 2;
		grid-row: 2;
		justify-self: start;
	}

	.foot-bgg img {
		width: 92px;
	}

}

</style>
